import { useAddOrModifyKlus } from "@personeel/hooks/useFetchData.ts";
import { usePersoneelsdienstenStore } from "@personeel/stores/PersoneelsdienstenStore.ts";
import { useKlusSelectedKlusStore } from "@personeel/stores/SelectedKlusStore.ts";
import { RefObject, useEffect, useRef, useState } from "react";
import { KlusModel } from "@personeel/models/KlusModel";
import { calcPosition, calcWidth } from "@personeel/utils/TijdlijnDateCalcUtils";
import { usePersoneelsinstellingenStore } from "@personeel/stores/PersoneelsinstellingenStore.ts";
import {
  Periode,
  DienstVoorNatijdPeriode,
  UseKlusDragOptions,
} from "@personeel/components/Werklijnen/hooks/useKlusDrag.ts";
import { personeelsdienstLabelWidth } from "@personeel/components/Werklijnen/Personeelsdiensten/PersoneelsdienstLabel/PersoneelsdientLabel.tsx";

const SCROLL_SPEED = 20; // pixels per animatieframe tijdens auto-scroll

const getContainer = () => document.getElementById("right-div");

function calcSchuifRuimte(
  dienstPeriode: Periode,
  timeLinePeriode: Periode,
  dienstVoorNatijdPeriode: DienstVoorNatijdPeriode,
): Periode {
  // Gebruik de meest beperkende grens: dienst of tijdlijn, afhankelijk van wat binnen de tijdlijn valt
  const effectiefBegin =
    dienstPeriode.begin !== null && dienstPeriode.begin > timeLinePeriode.begin
      ? dienstPeriode.begin.plus({ minutes: dienstVoorNatijdPeriode.voortijd })
      : timeLinePeriode.begin;
  const effectiefEind =
    dienstPeriode.eind !== null && dienstPeriode.eind < timeLinePeriode.eind
      ? dienstPeriode.eind.minus({ minutes: dienstVoorNatijdPeriode.natijd })
      : timeLinePeriode.eind;
  return { begin: effectiefBegin, eind: effectiefEind };
}

/**
 * Klamp het aantal minuten verschuiving zodat de klus binnen de meest beperkende
 * grens (dienst óf tijdlijn) blijft.
 */
export function clampMinuten(
  minutes: number,
  klus: KlusModel,
  dienstPeriode: Periode,
  timeLinePeriode: Periode,
  dienstVoorNatijdPeriode: DienstVoorNatijdPeriode,
): number {
  const klusDuur = klus.eindDatumtijd.diff(klus.beginDatumtijd, "minutes").minutes;
  const schuifRuimte = calcSchuifRuimte(dienstPeriode, timeLinePeriode, dienstVoorNatijdPeriode);

  // min: hoeveel minuten de klus maximaal naar links mag (negatief als beginDatumtijd na schuifruimte.begin ligt)
  const min = schuifRuimte.begin.diff(klus.beginDatumtijd, "minutes").minutes;
  // max: hoeveel minuten de klus maximaal naar rechts mag zodat de klus nog net binnen schuifruimte.eind past
  const max = schuifRuimte.eind.diff(klus.beginDatumtijd, "minutes").minutes - klusDuur;
  return Math.min(Math.max(minutes, min), max);
}

export interface UseKlusDragTijdResult {
  /** Visuele pixel-verschuiving t.o.v. de basispositie tijdens het slepen */
  dragOffsetPx: number;
  /** True terwijl er daadwerkelijk gesleept wordt */
  isDragging: boolean;
  /**
   * Synchrone ref: true tijdens actieve drag — gebruik dit in useEffect guards om vuren tijdens drag te voorkomen
   * o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
   */
  isDraggingRef: RefObject<boolean>;
  /**
   * Starten van de horizontale drag.
   * @param startClientX - de clientX bij het begin van de drag (mousedown-positie)
   * @param startScrollLeft - de scrollLeft van de container bij het begin van de drag
   */
  startDrag: (startClientX: number, startScrollLeft: number) => void;
  /**
   * Verwerk een muisbeweging tijdens de drag.
   * @param clientX - huidige clientX van de muis
   */
  onDragMove: (clientX: number) => void;
  /**
   * Afronden van de drag bij mouseup.
   * @param clientX - clientX bij loslaten van de muisknop
   */
  onDragEnd: () => void;
  /** Annuleren van de drag */
  cancelDrag: () => void;
}

/**
 * Hook voor het horizontaal (in tijd) verschuiven van een klus.
 * Beheert: dragOffset, auto-scroll, clamping op dienst-/tijdlijngrenzen en de API-aanroep.
 * De dragmode-detectie zit bewust NIET in deze hook; die verantwoordelijkheid ligt bij de aanroeper.
 */
export function useKlusDragTijd({
  klus,
  timeLinePeriode,
  scale,
  onDragOffsetChange,
  onDragMinutesChange,
  onFinalOffsetSet,
}: UseKlusDragOptions): UseKlusDragTijdResult {
  const { mutate: addOrModifyKlus } = useAddOrModifyKlus();
  const { personeelsinstellingen } = usePersoneelsinstellingenStore();
  const { setDragOffsetMinutes } = useKlusSelectedKlusStore();

  // dragOffsetPx als state: alleen gebruikt voor eindpositie na drop (triggert useEffect in Klus.tsx)
  // Tijdens drag wordt de positie via onDragOffsetChange direct op de DOM gezet (geen re-render per frame)
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  // isDragging: true tijdens een actieve drag, gebruikt voor de klus-dragging CSS-klasse
  const [isDragging, setIsDragging] = useState(false);

  // dragOffsetPxRef: synchrone referentie naar de huidige offset — niet afhankelijk van React render-cyclus
  const dragOffsetPxRef = useRef(0);
  // isDraggingRef: synchrone referentie — voorkomt dat useEffect in Klus.tsx vuurt tijdens actieve drag
  // o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
  const isDraggingRef = useRef(false);

  // dragStartX/dragStartScrollLeft: referentiepunten bij start van drag
  const dragStartX = useRef<number | null>(null);
  const dragStartScrollLeft = useRef<number>(0);
  // currentClientX: bijgehouden muispositie voor gebruik in de auto-scroll animatieloop
  const currentClientX = useRef<number>(0);
  // autoScrollRaf: requestAnimationFrame id van de actieve auto-scroll loop
  const autoScrollRaf = useRef<number | null>(null);

  // Zoek de dienst op via de dienstSleutelIndex om de grenzen (dienstBegin/dienstEind)
  // te bepalen waarbinnen de klus mag worden verschoven
  const dienstSleutelIndex = usePersoneelsdienstenStore((state) => state.dienstSleutelIndex);
  const dienst = klus.dienst ? dienstSleutelIndex.get(klus.dienst) : undefined;
  // Bij niet toegewezen klus (in niet toegewezen klussen-row) schuiven over hele tijdlijn?????
  const dienstPeriode: Periode = dienst
    ? { begin: dienst.starttijd, eind: dienst.eindtijd }
    : { begin: timeLinePeriode.begin, eind: timeLinePeriode.eind };
  const dienstVoorNaTijdperiode: DienstVoorNatijdPeriode = {
    voortijd: personeelsinstellingen.voortijd,
    natijd: personeelsinstellingen.natijd,
  };

  // Bereken de basispositie zodat berekenEnZetOffset de scroll-naar-klus logica kan toepassen
  const baseLeft = calcPosition(
    klus.beginDatumtijd,
    timeLinePeriode.begin,
    timeLinePeriode.eind,
    scale,
  );
  const width = calcWidth(klus.beginDatumtijd, klus.eindDatumtijd, timeLinePeriode.eind, scale);

  // Reset dragOffsetPx zodra de store de nieuwe beginDatumtijd heeft verwerkt, anders springt de klus
  // even terug naar zijn oorspronkelijke positie
  useEffect(() => {
    dragOffsetPxRef.current = 0;
    setDragOffsetPx(0);
  }, [klus.beginDatumtijd]);

  // Ruim de auto-scroll animatieloop op als het component unmount
  useEffect(() => {
    return () => stopAutoScroll();
  }, []);

  function berekenEnZetOffset(clientX: number) {
    const container = getContainer();
    if (!container) return;
    // scrollDelta compenseert voor scrolling die plaatsvond nadat de drag begon:
    // als bv de container 50px naar rechts gescrolld is, is de klus 50px verder mee verschoven —
    // dat moet worden meegeteld in dx zodat de klus onder de muis blijft
    const scrollDelta = (container?.scrollLeft ?? 0) - dragStartScrollLeft.current;
    const dx = clientX - (dragStartX.current ?? 0) + scrollDelta;

    // Gebruik ruwe pixels voor vloeiende visuele beweging (geen afronding naar minuten).
    // Klamp alleen op de grenzen (in pixels) zodat de klus niet buiten dienst/tijdlijn kan.
    const clampedMinutes = clampMinuten(
      dx / scale,
      klus,
      dienstPeriode,
      timeLinePeriode,
      dienstVoorNaTijdperiode,
    );
    const newOffsetPx = clampedMinutes * scale;

    // Synchroon scrollen zodat klus altijd binnen zichtbaar gebied blijft
    const rect = container.getBoundingClientRect();
    const klusViewportLeft =
      rect.left + personeelsdienstLabelWidth + baseLeft + newOffsetPx - container.scrollLeft;
    const klusViewportRight = klusViewportLeft + width;

    if (klusViewportRight > rect.right) {
      // Rechterkant klus buiten rechterrand: scroll direct mee
      const scrollAanpassing = klusViewportRight - rect.right;
      dragStartScrollLeft.current += scrollAanpassing; // neutraliseer effect op volgende scrollDelta
    } else if (klusViewportLeft < rect.left + personeelsdienstLabelWidth) {
      // Linkerkant klus buiten linkerrand tijdlijn: scroll direct mee
      const scrollAanpassing = rect.left + personeelsdienstLabelWidth - klusViewportLeft;
      dragStartScrollLeft.current -= scrollAanpassing; // neutraliseer effect op volgende scrollDelta
    }

    dragOffsetPxRef.current = newOffsetPx;
    onDragOffsetChange?.(newOffsetPx);
    const roundedMinutes = Math.round(clampedMinutes);
    onDragMinutesChange?.(roundedMinutes);
    setDragOffsetMinutes(roundedMinutes);
  }

  // t.b.v. scrollen van venster als klus over de rand van zichtbare gedeelte wordt gesleept
  function startAutoScroll() {
    // Auto-scroll animatieloop: scrollt continu zolang de klus de scroll-zone raakt
    function loop() {
      const container = getContainer();
      // Stop direct als drag al beëindigd is (dragStartX wordt null gezet vóór stopAutoScroll)
      if (!container || dragStartX.current === null) return;

      const rect = container.getBoundingClientRect();

      // Bereken actuele kluspositie in de viewport op basis van huidige dragOffset
      const scrollDelta = container.scrollLeft - dragStartScrollLeft.current;
      const dx = currentClientX.current - dragStartX.current + scrollDelta;

      const rawMinutes = dx / scale;
      const clampedMinutes = clampMinuten(
        rawMinutes,
        klus,
        dienstPeriode,
        timeLinePeriode,
        dienstVoorNaTijdperiode,
      );
      const offsetPx = clampedMinutes * scale;

      const klusViewportLeft =
        rect.left + personeelsdienstLabelWidth + baseLeft + offsetPx - container.scrollLeft;

      // Klus heeft absolute rechtergrens bereikt als clamping actief is naar rechts
      const isGeclampRechts = rawMinutes > clampedMinutes;
      // Klus heeft absolute linkergrens bereikt als clamping actief is naar links
      const isGeclampLinks = rawMinutes < clampedMinutes;

      const schuifRuimte = calcSchuifRuimte(
        dienstPeriode,
        timeLinePeriode,
        dienstVoorNaTijdperiode,
      );

      // Offset in minuten bij de linker- en rechtergrens
      const minOffset = schuifRuimte.begin.diff(klus.beginDatumtijd, "minutes").minutes;
      const maxOffset =
        schuifRuimte.eind.diff(klus.beginDatumtijd, "minutes").minutes -
        klus.eindDatumtijd.diff(klus.beginDatumtijd, "minutes").minutes;
      const EPSILON = 0.01; // kleine marge voor floating point

      // Is de klus geclampt op de linkergrens (voortijd)?
      const isGeclampOpVoortijd = isGeclampLinks && Math.abs(clampedMinutes - minOffset) < EPSILON;
      // Is de klus geclampt op de rechtergrens (natijd)?
      const isGeclampOpNatijd = isGeclampRechts && Math.abs(clampedMinutes - maxOffset) < EPSILON;

      // Pixel-positie van de effectieve grenzen in de tijdlijn
      const effectiefBeginPx = calcPosition(
        schuifRuimte.begin,
        timeLinePeriode.begin,
        timeLinePeriode.eind,
        scale,
      );
      const effectiefEindPx = calcPosition(
        schuifRuimte.eind,
        timeLinePeriode.begin,
        timeLinePeriode.eind,
        scale,
      );

      // Is het voortijd-clamppunt zichtbaar? (linkerrand tijdlijn t/m rechterrand viewport)
      const isVoortijdZichtbaar =
        effectiefBeginPx >= container.scrollLeft &&
        effectiefBeginPx <=
          container.scrollLeft + container.clientWidth - personeelsdienstLabelWidth;

      // Is het natijd-clamppunt zichtbaar? (linkerrand tijdlijn t/m rechterrand viewport)
      const isNatijdZichtbaar =
        effectiefEindPx >= container.scrollLeft &&
        effectiefEindPx <=
          container.scrollLeft + container.clientWidth - personeelsdienstLabelWidth;
      const klusViewportRight = klusViewportLeft + width;

      if (isGeclampOpVoortijd && !isVoortijdZichtbaar) {
        // extra scroll naar rechts zodat voortijdTijdstip zichtbaar wordt (is duidelijker  te zien
        // waarom niet meer verder geschoven kan worden)
        container.scrollLeft -= SCROLL_SPEED;
      } else if (isGeclampOpNatijd && !isNatijdZichtbaar) {
        // extra scroll naar links zodat natijdTijdstip zichtbaar wordt (is duidelijker  te zien
        // waarom niet meer verder geschoven kan worden)
        container.scrollLeft += SCROLL_SPEED;
      } else if (!isGeclampRechts && klusViewportRight >= rect.right) {
        // Rechterkant klus raakt rechterrand: scroll venster naar links
        container.scrollLeft += SCROLL_SPEED;
        berekenEnZetOffset(currentClientX.current);
      } else if (!isGeclampLinks && klusViewportLeft <= rect.left + personeelsdienstLabelWidth) {
        // Linkerkant klus raakt linkerrand tijdlijn: scroll venster naar rechts
        container.scrollLeft -= SCROLL_SPEED;
        berekenEnZetOffset(currentClientX.current);
      }

      autoScrollRaf.current = requestAnimationFrame(loop);
    }
    autoScrollRaf.current = requestAnimationFrame(loop);
  }

  function stopAutoScroll() {
    if (autoScrollRaf.current !== null) {
      cancelAnimationFrame(autoScrollRaf.current);
      autoScrollRaf.current = null;
    }
  }

  function startDrag(startClientX: number, startScrollLeft: number) {
    dragStartX.current = startClientX;
    dragStartScrollLeft.current = startScrollLeft;
    isDraggingRef.current = true;
    setIsDragging(true);
    startAutoScroll();
  }

  function onDragMove(clientX: number) {
    currentClientX.current = clientX;
    berekenEnZetOffset(clientX);
  }

  function onDragEnd() {
    // Markeer drag als beëindigd EERST zodat in-flight RAF-callbacks van auto-scroll niets meTimeLineer doen
    // o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
    dragStartX.current = null;
    isDraggingRef.current = false;
    // Stop auto-scroll nadat dragStartX null is gezet (loop checkt dragStartX === null als stopconditie)
    stopAutoScroll();

    // Gebruik de laatste bekende offset uit de ref — dit is exact wat nu op de DOM staat.
    // Niet opnieuw berekenen vanuit clientX: dat geeft een andere waarde dan de zichtbare positie.
    const currentOffsetPx = dragOffsetPxRef.current;
    const minutes = Math.round(currentOffsetPx / scale);

    if (minutes === 0) {
      dragOffsetPxRef.current = 0;
      setDragOffsetPx(0);
      onFinalOffsetSet?.(0);
    } else {
      // Snap de DOM naar de afgeronde minutenpositie (één pixel-correctie mogelijk)
      const snappedPx = minutes * scale;
      dragOffsetPxRef.current = snappedPx;
      setDragOffsetPx(snappedPx);
      onFinalOffsetSet?.(snappedPx);
      const verschovenKlus = {
        ...klus,
        beginDatumtijd: klus.beginDatumtijd.plus({ minutes }),
        eindDatumtijd: klus.eindDatumtijd.plus({ minutes }),
      };

      addOrModifyKlus(verschovenKlus);
    }

    setIsDragging(false);
    // Reset drag-offset in store; sidepanel toont nu de bijgewerkte selectedKlus tijden
    setDragOffsetMinutes(0);
  }

  function cancelDrag() {
    stopAutoScroll();
    dragStartX.current = null;
    isDraggingRef.current = false;
    dragOffsetPxRef.current = 0;
    setDragOffsetPx(0);
    setIsDragging(false);
    setDragOffsetMinutes(0);
  }

  return { dragOffsetPx, isDragging, isDraggingRef, startDrag, onDragMove, onDragEnd, cancelDrag };
}
